import type { StoreApi } from 'zustand'
import type { createVoucherApiClient } from '../../infrastructure/vouchers/voucherApiClient'
import type { PaymentOptions, PaymentScopePage } from '../types'
import type { VoucherFormValues, VoucherStore, VoucherType } from './types'
import type { AnalyticsClient, DateFormat } from '@/scopes/common/domain/types'
import type { LocationResolver } from '@/scopes/common/infrastructure/dom/location'
import ga4analyticsClient from '@/scopes/common/infrastructure/analytics/ga4analyticsClient'
import { getAmountValue, getSelectedAmount } from '../adyen/policies/paymentSelectors'
import { createPaymentEvent, createPaymentMethodEvent, createVoucherSelectedEvent, getPaymentEventContext } from '../analytics'
import { isValidVoucherBirthdate, toApiVoucherBirthdate, VOUCHER_EMAIL_PATTERN } from './policies/voucherForm'
import { calculateVoucherRedemption } from './policies/voucherRedemption'

interface Dependencies {
  apiClient: ReturnType<typeof createVoucherApiClient>
  store: StoreApi<VoucherStore>
  location: LocationResolver
  bookingNumber: string
  groupUserId: string | null
  paymentOptions: PaymentOptions
  selectedAmountType: string
  customDepositAmount: number | null
  dateFormat: DateFormat
  scope?: PaymentScopePage
  analyticsClient?: AnalyticsClient
}

export function createVoucherActions({ apiClient, store, location, bookingNumber, groupUserId, paymentOptions, selectedAmountType, customDepositAmount, dateFormat, scope, analyticsClient = ga4analyticsClient }: Dependencies) {
  const context = getPaymentEventContext(scope ?? 'BookingPayment')
  function voucherFormChanged() {
    if (store.getState().genericError)
      store.setState({ genericError: false })
  }

  async function voucherCheckRequested(values: VoucherFormValues) {
    const form = {
      voucherCode: values.voucherCode.trim(),
      email: values.email.trim(),
      birthdate: values.birthdate.trim(),
    }

    if (!form.voucherCode || !VOUCHER_EMAIL_PATTERN.test(form.email) || !isValidVoucherBirthdate(form.birthdate, dateFormat)) {
      store.setState({ genericError: true })
      analyticsClient.sendEvent(createPaymentEvent({
        description: 'voucher check',
        element: 'form',
        type: 'manual voucher',
        result: 'validation error',
        interaction: true,
        context,
      }))
      return
    }

    store.setState({ checkLoading: true, genericError: false })
    try {
      const { voucher, isValid } = await apiClient.getVoucherPaymentsBalance({
        voucherCode: form.voucherCode,
        email: form.email,
        birthdate: toApiVoucherBirthdate(form.birthdate, dateFormat) ?? '',
      })
      store.setState({ checkLoading: false, voucher, screen: isValid && voucher.canBeUsed ? 'options' : 'unusable', genericError: !isValid })
      analyticsClient.sendEvent(createPaymentEvent({
        description: 'voucher check',
        element: 'form',
        type: 'manual voucher',
        result: isValid ? (voucher.canBeUsed ? 'valid' : 'unusable') : 'invalid',
        interaction: true,
        context,
      }))
    }
    catch {
      store.setState({ checkLoading: false, genericError: true })
      analyticsClient.sendEvent(createPaymentEvent({
        description: 'voucher check',
        element: 'form',
        type: 'manual voucher',
        result: 'error',
        interaction: true,
        context,
      }))
    }
  }

  async function voucherPaymentRequested() {
    const state = store.getState()
    if (!state.voucher)
      return
    const selectedAmount = getSelectedAmount(paymentOptions.paymentAmounts, selectedAmountType)
    const bookingAmount = getAmountValue(selectedAmount, customDepositAmount)
    if (!bookingAmount || bookingAmount <= 0) {
      store.setState({ applyError: true })
      analyticsClient.sendEvent(createPaymentEvent({
        description: 'voucher payment result',
        element: 'voucher form',
        type: 'voucher',
        result: 'validation error',
        interaction: true,
        context,
      }))
      return
    }
    const { amountToPay, voucherOption } = calculateVoucherRedemption(state.voucher.amount, bookingAmount)
    store.setState({ applyLoading: true, applyError: false })
    try {
      analyticsClient.sendEvent(createPaymentMethodEvent({
        paymentMethod: 'voucher',
        paymentType: voucherOption === 'PARTIAL_VOUCHER' ? 'deposit' : 'full',
        amountPaid: amountToPay,
        manual: voucherOption === 'PARTIAL_VOUCHER' ? 'yes' : 'no',
        isOwned: state.selectedVoucherIsOwned,
      }))

      const { result, isWellApplied } = await apiClient.applyVoucherPayment({
        bookingNumber,
        groupUserId,
        paymentmethod: 'voucher',
        voucherCode: state.voucher.voucherCode,
        birthdate: state.voucher.birthdate,
        email: state.voucher.email,
        voucherOption,
        amountToPay,
        referrerUrl: location.href(),
      })
      if (result.urlRedirect) {
        analyticsClient.sendEvent(createPaymentEvent({
          description: 'voucher payment result',
          element: 'payment result banner',
          type: 'voucher',
          result: 'redirect',
          interaction: false,
          context,
        }))
        location.redirectTo(result.urlRedirect)
        return
      }
      if (!isWellApplied)
        throw new Error('Voucher was not applied')
      store.setState({ applyLoading: false, screen: 'success', applyResult: result })
      analyticsClient.sendEvent(createPaymentEvent({
        description: 'voucher payment result',
        element: 'payment result banner',
        type: 'voucher',
        result: 'success',
        interaction: false,
        context,
      }))
    }
    catch {
      store.setState({ applyLoading: false, applyError: true })
      analyticsClient.sendEvent(createPaymentEvent({
        description: 'voucher payment result',
        element: 'payment result banner',
        type: 'voucher',
        result: 'error',
        interaction: false,
        context,
      }))
    }
  }

  function voucherFlowCanceled() {
    store.getState().reset()
    analyticsClient.sendEvent(createPaymentEvent({
      description: 'voucher flow',
      element: 'button',
      type: 'voucher',
      result: 'cancelled',
      interaction: true,
      context,
    }))
  }

  function availableVouchersViewed() {
    store.getState().reset()
    analyticsClient.sendEvent(createPaymentEvent({
      description: 'voucher flow',
      element: 'button',
      type: 'voucher',
      result: 'available vouchers viewed',
      interaction: true,
      context,
    }))
  }

  function ownedVoucherSelected(voucher: VoucherType | undefined) {
    if (!voucher)
      return
    store.setState({ voucher, screen: voucher.canBeUsed ? 'options' : 'unusable', genericError: false, selectedVoucherIsOwned: true })
    analyticsClient.sendEvent(createVoucherSelectedEvent({ isOwned: true }))
  }

  function manualVoucherFlowOpened() {
    store.setState({ selectedVoucherIsOwned: false })
    analyticsClient.sendEvent(createVoucherSelectedEvent({ isOwned: false }))
  }

  function voucherFormValidationFailed() {
    analyticsClient.sendEvent(createPaymentEvent({
      description: 'voucher check',
      element: 'form',
      type: 'manual voucher',
      result: 'validation error',
      interaction: true,
      context,
    }))
  }

  return {
    voucherFormChanged,
    voucherCheckRequested,
    voucherPaymentRequested,
    voucherFlowCanceled,
    availableVouchersViewed,
    ownedVoucherSelected,
    manualVoucherFlowOpened,
    voucherFormValidationFailed,
  }
}
